import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

import { API_URL } from '../../config/api';
import { readChannelsFromRealm, realmAvailable, upsertRealmDocs } from '../../realm';
import {
  dedupeDirectMessages as dedupeDmList,
  extractEntityId,
  findDirectMessageForPeer,
  isWorkloopDm,
} from '../../utils/directMessageUtils';
import { isTaskCommentChannel } from '../../utils/channelTypeUtils';

import { addMessage, addOptimisticMessage } from './chatSlice';

const isDirectMessageChannel = (channel) => {
  if (isWorkloopDm(channel)) return true;
  const raw = channel?.type;
  const type = (
    typeof raw === 'string'
      ? raw
      : (raw && typeof raw === 'object' ? (raw.name || raw.type || '') : String(raw || ''))
  ).toString().toUpperCase();
  return type.includes('DIRECT') || type === 'DM';
};

const nonDmChannels = (list) =>
  (Array.isArray(list) ? list : []).filter((c) => !isDirectMessageChannel(c) && !isTaskCommentChannel(c));

const dmChannels = (list, currentUserId, users) =>
  dedupeDmList(
    (Array.isArray(list) ? list : []).filter((channel) => (
      isDirectMessageChannel(channel) || isWorkloopDm(channel)
    )),
    currentUserId,
    users,
  );

const parseChannelApiError = (error, fallback) => {
  const data = error.response?.data;
  if (typeof data === 'string') {
    return data.replace(/^Error:\s*/, '');
  }
  if (data?.error) {
    return typeof data.error === 'string' ? data.error : fallback;
  }
  if (data?.message) {
    return data.message;
  }
  if (error.response?.status === 403) {
    return 'Access denied. Only the channel creator can manage channel admins.';
  }
  return fallback;
};

// Async thunks
export const fetchChannels = createAsyncThunk(
  'channels/fetchChannels',
  async (_, { rejectWithValue }) => {
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
    let cached = null;
    try {
      cached = await readChannelsFromRealm();
    } catch {
      cached = null;
    }

    if (offline) {
      return nonDmChannels(cached || []);
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/channels`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = Array.isArray(response.data) ? response.data : [];
      upsertRealmDocs('Channel', list).catch(() => {});
      return nonDmChannels(list);
    } catch (error) {
      if (cached && cached.length) {
        return nonDmChannels(cached);
      }
      if (await realmAvailable()) {
        const again = await readChannelsFromRealm();
        if (again?.length) return nonDmChannels(again);
      }
      return rejectWithValue(error.response?.data || 'Failed to fetch channels');
    }
  }
);

export const createChannel = createAsyncThunk(
  'channels/createChannel',
  async ({ name, description, imageUrl, isPrivate = true, memberIds = [], spaceId, spaceName }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/channels`,
        { name, description, imageUrl, isPrivate, memberIds, spaceId, spaceName },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      upsertRealmDocs('Channel', [response.data]);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create channel');
    }
  }
);

const persistChannelWrite = async (channel) => {
  if (channel) await upsertRealmDocs('Channel', [channel]);
};

export const removeMember = createAsyncThunk(
  'channels/removeMember',
  async ({ channelId, userId }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `${API_URL}/channels/${channelId}/members/${userId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      persistChannelWrite(response.data);
      return { channelId, channel: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to remove member');
    }
  }
);

export const grantChannelAdmin = createAsyncThunk(
  'channels/grantChannelAdmin',
  async ({ channelId, userId }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/channels/${channelId}/channel-admins/${userId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      persistChannelWrite(response.data);
      return { channelId, channel: response.data };
    } catch (error) {
      return rejectWithValue(parseChannelApiError(error, 'Failed to grant admin status'));
    }
  }
);

export const revokeChannelAdmin = createAsyncThunk(
  'channels/revokeChannelAdmin',
  async ({ channelId, userId }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        `${API_URL}/channels/${channelId}/channel-admins/${userId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      persistChannelWrite(response.data);
      return { channelId, channel: response.data };
    } catch (error) {
      return rejectWithValue(parseChannelApiError(error, 'Failed to revoke admin status'));
    }
  }
);

export const fetchDirectMessages = createAsyncThunk(
  'channels/fetchDirectMessages',
  async (_, { getState, rejectWithValue }) => {
    const state = getState();
    const userId = state.auth?.user?.id;
    const users = state.users?.users || [];
    let cached = null;
    try {
      cached = await readChannelsFromRealm();
    } catch {
      cached = null;
    }
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false;

    try {
      if (offline) {
        return dmChannels(cached || [], userId, users);
      }

      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/channels/direct-messages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = Array.isArray(response.data) ? response.data : [];
      await upsertRealmDocs('Channel', list);
      return dmChannels(list, userId, users);
    } catch (error) {
      if (cached && cached.length) {
        return dmChannels(cached, userId, users);
      }
      return rejectWithValue(error.response?.data || 'Failed to fetch direct messages');
    }
  }
);

export const getOrCreateDirectMessage = createAsyncThunk(
  'channels/getOrCreateDirectMessage',
  async (userId, { getState, rejectWithValue }) => {
    const state = getState();
    const peerId = extractEntityId(userId);
    const me = extractEntityId(state.auth?.user?.id);
    if (!peerId) {
      return rejectWithValue('Cannot start a direct message without a user id.');
    }

    const localLists = [
      state.channels?.directMessages,
    ];
    try {
      const cached = await readChannelsFromRealm();
      if (cached?.length) localLists.push(cached);
    } catch {
      // ignore
    }
    const existing = findDirectMessageForPeer(localLists.flat(), me, peerId);
    if (existing?.id || existing?._id) {
      return existing;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/channels/direct-messages/${peerId}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      await persistChannelWrite(response.data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to create direct message');
    }
  }
);

export const joinChannel = createAsyncThunk(
  'channels/joinChannel',
  async (channelId, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/channels/${channelId}/join`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      persistChannelWrite(response.data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to join channel');
    }
  }
);

export const updateChannel = createAsyncThunk(
  'channels/updateChannel',
  async ({ channelId, name, description, imageUrl, isPrivate, memberIds, spaceId, spaceName, applyExisting = false }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_URL}/channels/${channelId}`,
        { name, description, imageUrl, isPrivate, memberIds, spaceId, spaceName, applyExisting: Boolean(applyExisting) },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      upsertRealmDocs('Channel', [response.data]);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.response?.data || 'Failed to update channel';
      return rejectWithValue(typeof errorMessage === 'string' ? errorMessage : 'Failed to update channel');
    }
  }
);

export const updateChannelSpace = createAsyncThunk(
  'channels/updateChannelSpace',
  async ({ channelId, spaceId, spaceName, applyExisting = false }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.patch(
        `${API_URL}/channels/${channelId}/space`,
        { spaceId: spaceId || '', spaceName: spaceName || '', applyExisting: Boolean(applyExisting) },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      upsertRealmDocs('Channel', [response.data]);
      return response.data;
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.response?.data || 'Failed to update channel space';
      return rejectWithValue(typeof errorMessage === 'string' ? errorMessage : 'Failed to update channel space');
    }
  }
);

const initialState = {
  channels: [],
  directMessages: [],
  loading: false,
  error: null,
};

const channelSlice = createSlice({
  name: 'channels',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    addChannel: (state, action) => {
      const channel = action.payload;
      if (!channel || isDirectMessageChannel(channel) || isTaskCommentChannel(channel)) return;
      if (state.channels.some((c) => c.id === channel.id)) return;
      state.channels.push(channel);
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch channels
      .addCase(fetchChannels.pending, (state) => {
        if (!(state.channels || []).length) state.loading = true;
        state.error = null;
      })
      .addCase(fetchChannels.fulfilled, (state, action) => {
        state.loading = false;
        state.channels = nonDmChannels(action.payload);
        state.error = null;
        console.log('✅ Channels updated in store:', state.channels.length);
      })
      .addCase(fetchChannels.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        console.error('❌ Failed to fetch channels:', action.payload);
        // Do not wipe channels already shown in the sidebar on a failed refetch.
        if (!Array.isArray(state.channels)) {
          state.channels = [];
        }
      })
      // Create channel
      .addCase(createChannel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createChannel.fulfilled, (state, action) => {
        state.loading = false;
        const channel = action.payload;
        if (channel && !isDirectMessageChannel(channel) && !isTaskCommentChannel(channel)
            && !state.channels.some((c) => c.id === channel.id)) {
          state.channels.push(channel);
        }
        state.error = null;
      })
      .addCase(createChannel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Join channel
      .addCase(joinChannel.fulfilled, (state, action) => {
        const channel = action.payload;
        if (!channel || isTaskCommentChannel(channel) || isDirectMessageChannel(channel)) {
          return;
        }
        const index = state.channels.findIndex(c => c.id === channel.id);
        if (index !== -1) {
          state.channels[index] = channel;
        } else {
          state.channels.push(channel);
        }
      })
      // Remove member
      .addCase(removeMember.fulfilled, (state, action) => {
        const { channelId, channel } = action.payload;
        const index = state.channels.findIndex(c => c.id === channelId);
        if (index !== -1) {
          state.channels[index] = channel;
        }
      })
      // Grant channel admin
      .addCase(grantChannelAdmin.fulfilled, (state, action) => {
        const { channelId, channel } = action.payload;
        const index = state.channels.findIndex(c => c.id === channelId);
        if (index !== -1) {
          state.channels[index] = channel;
        }
      })
      // Revoke channel admin
      .addCase(revokeChannelAdmin.fulfilled, (state, action) => {
        const { channelId, channel } = action.payload;
        const index = state.channels.findIndex(c => c.id === channelId);
        if (index !== -1) {
          state.channels[index] = channel;
        }
      })
      // Fetch direct messages
      .addCase(fetchDirectMessages.pending, (state) => {
        if (!(state.directMessages || []).length) state.loading = true;
        state.error = null;
      })
      .addCase(fetchDirectMessages.fulfilled, (state, action) => {
        state.loading = false;
        // Thunk already dedupes with the current user id. Re-running without
        // it collapses 1:1 rows that share a display name (self-chat vs admin).
        state.directMessages = Array.isArray(action.payload) ? action.payload : [];
        state.error = null;
        console.log('✅ Direct messages updated in store:', state.directMessages.length);
      })
      .addCase(fetchDirectMessages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        console.error('❌ Failed to fetch direct messages:', action.payload);
      })
      // Get or create direct message
      .addCase(getOrCreateDirectMessage.fulfilled, (state, action) => {
        const channel = action.payload;
        if (!channel) return;
        const id = extractEntityId(channel);
        const rest = id
          ? state.directMessages.filter((dm) => extractEntityId(dm) !== id)
          : state.directMessages;
        state.directMessages = [channel, ...rest];
      })
      // Update channel
      .addCase(updateChannel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateChannel.fulfilled, (state, action) => {
        state.loading = false;
        const updatedChannel = action.payload;
        const index = state.channels.findIndex(c => c.id === updatedChannel.id);
        if (index !== -1) {
          state.channels[index] = updatedChannel;
        }
        state.error = null;
      })
      .addCase(updateChannel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateChannelSpace.fulfilled, (state, action) => {
        const updatedChannel = action.payload;
        const index = state.channels.findIndex((c) => c.id === updatedChannel.id);
        if (index !== -1) {
          state.channels[index] = updatedChannel;
        }
      })
      .addCase(addMessage, (state, action) => {
        const channelId = action.payload?.channelId;
        const message = action.payload?.message;
        const ts = message?.createdAt || message?.timestamp;
        if (!channelId || !ts) return;
        const next = new Date(ts).getTime();
        const patch = (list) => {
          if (!Array.isArray(list)) return;
          const item = list.find((channel) => channel?.id === channelId);
          if (!item) return;
          const prev = new Date(item.lastMessageAt || item.updatedAt || 0).getTime();
          if (Number.isFinite(next) && next >= (Number.isFinite(prev) ? prev : 0)) {
            item.lastMessageAt = ts;
            item.updatedAt = ts;
          }
        };
        patch(state.channels);
        patch(state.directMessages);
      })
      .addCase(addOptimisticMessage, (state, action) => {
        const channelId = action.payload?.channelId;
        const message = action.payload?.message;
        const ts = message?.createdAt || message?.timestamp;
        if (!channelId || !ts) return;
        const next = new Date(ts).getTime();
        const patch = (list) => {
          if (!Array.isArray(list)) return;
          const item = list.find((channel) => channel?.id === channelId);
          if (!item) return;
          const prev = new Date(item.lastMessageAt || item.updatedAt || 0).getTime();
          if (Number.isFinite(next) && next >= (Number.isFinite(prev) ? prev : 0)) {
            item.lastMessageAt = ts;
            item.updatedAt = ts;
          }
        };
        patch(state.channels);
        patch(state.directMessages);
      });
  },
});

export const { clearError, addChannel } = channelSlice.actions;
export default channelSlice.reducer;
